import { useEventListener, useLatest } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep, pick } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import {
    BaseScaleView,
    BaseStagePlugin,
    useFramework,
    useIsBasicOperate,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { registerStyleSheet } from '@/utils/common-kit';
import { isHotKeyPress } from '@/utils/event-kit';
import { BaseLayerViewType, LayerType, PluginType, type IExportPlugin, type IPoint } from '@/version-types';
import { getTableCellInfoListByPoint } from '../../utils/geometry';
import { getCellStyle } from '../../utils/table';
import CellEditor from './components/cell-editor';
import { registerTableContextMenu } from './components/context-menu';
import TableCellSelectedRect from './components/table-cell-selected-rect';
import TableController from './components/table-controller';
import { TABLE_CONTROLLER_EVENTS } from './events';
import { useActiveTableLayer, useTableEditNodeCursorCss, useTextBizHotKey } from './hooks';
import tableEditPluginState, { useTableEditState, type CellsSelectedInfo } from './state';
import { TableAction } from './table-action';
import { TableLayerEdit } from './table-edit';
import { appendCellsSelectedInfoByCellInfo, getCellsSelectedInfoByMouseRange } from './utils/cell';
import { getTableFramePoint } from './utils/get-table-frame-point';
import { isOnlyClick } from './utils/native';

const TableEditWidget = React.memo(() => {
    // --- framework states ---
    const framework = useFramework();
    const scale = useScale();

    const isBasicOperate = useIsBasicOperate();

    // --- framework actions ---
    const { getState } = useTableEditState();
    const clearTableEditState = useTableEditState().getState().initState;

    // --- internal states ---
    const { setState } = useTableEditState();
    const activeCell = useTableEditState()((s) => s.activeCell);
    const hasEditingText = useTableEditState()((s) => s.hasEditingText);
    const hasSelectedInfo = useTableEditState()((s) => !!s.cellsSelectedInfo || !!s.rowOrColSelectedRectInfo);
    const lineIsDragging = useTableEditState()((s) => s.lineIsDragging);
    const activeTableLayer = useActiveTableLayer();
    const isActiveTableLayer = activeTableLayer !== null;
    const [tableEditNode, setTableEditNode] = useState<HTMLElement | null>(null);
    const [isMouseWheelSetRowOrColCount, setIsMouseWheelSetRowOrColCount] = useState(false);
    const [isMoveHandleDragging, setIsMoveHandleDragging] = useState(false);
    const [cellEditorWidth, setCellEditorWidth] = useState<{ cellId: string; width: number } | null>(null);
    const isDisableMoveState = (hasSelectedInfo || lineIsDragging) && !isMoveHandleDragging;
    const activeCellId = activeCell?.id;
    const handleCellEditorWidthChange = useCallback(
        (width: number) => {
            if (!activeCellId) return;
            setCellEditorWidth((current) => {
                if (current?.cellId === activeCellId && current.width === width) return current;
                return { cellId: activeCellId, width };
            });
        },
        [activeCellId],
    );

    useEventListener('mouseup', () => setIsMoveHandleDragging(false), { target: () => document });
    useEventListener('blur', () => setIsMoveHandleDragging(false), { target: () => window });

    useEffect(() => {
        const startMouseWheelSetRowOrColCountListenerID = framework.service.events.on(
            TABLE_CONTROLLER_EVENTS.START_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT,
            () => {
                setIsMouseWheelSetRowOrColCount(true);
            },
        );
        const endMouseWheelSetRowOrColCountListenerID = framework.service.events.on(
            TABLE_CONTROLLER_EVENTS.END_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT,
            () => {
                setIsMouseWheelSetRowOrColCount(false);
            },
        );
        return () => {
            framework.service.events.off(startMouseWheelSetRowOrColCountListenerID);
            framework.service.events.off(endMouseWheelSetRowOrColCountListenerID);
        };
    }, []);

    useTableEditNodeCursorCss({
        tableEditNode,
        isActiveTableLayer,
        isDisableMoveState,
    });

    // --- internal refs ---
    const activeTableLayerRef = useLatest(activeTableLayer);
    const mouseDownActiveTableLayerIdRef = useRef<string | null>(null);

    useTextBizHotKey();

    useEffect(() => {
        const mouseDownListenerID = framework.service.editor.onMouseDown(() => {
            mouseDownActiveTableLayerIdRef.current = activeTableLayerRef.current?.id ?? null;
        }, 99);

        return () => framework.service.events.off(mouseDownListenerID);
    }, [activeTableLayerRef, framework.service.editor, framework.service.events]);

    useEffect(() => {
        if (activeTableLayer?.id) {
            const removeStyleSheet = registerStyleSheet(`
                .active-operate {
                    border-color: transparent;
                }
            `);
            return () => {
                clearTableEditState();
                removeStyleSheet();
            };
        }
    }, [activeTableLayer?.id]);

    useEffect(() => {
        if (!isActiveTableLayer) return;
        if (!isDisableMoveState) return;
        framework.service.editor.setDisableMove(true);
        return () => framework.service.editor.setDisableMove(false);
    }, [isActiveTableLayer, isDisableMoveState]);

    useEffect(() => {
        const baseOperateListenerID = framework.service.frame.onBaseOperate((layerId) => {
            if (framework.service.flatLayer.getLayer(layerId).layerType !== LayerType.TABLE) return;

            setState({
                hasEditingText: false,
                baseCellsBeforeEditing: null,
            });
        });
        // 撤销or重做时, 取消单元格选择态 (issue: https://jira.meitu.com/browse/MTXXPRO-30744)
        const historyListenerID = framework.service.history.onBeforeChange(({ isRedo, isUndo }) => {
            if (!activeTableLayerRef.current) return;
            if (isRedo || isUndo) {
                clearTableEditState();
            }
        });
        return () => {
            framework.service.events.off(baseOperateListenerID);
            framework.service.events.off(historyListenerID);
        };
    }, []);

    // 记录表格移动开始时的位置：否则，选中单元格的情况下，移动表格会导致选中位置发生改变
    const moveStartLayerInfo = useRef<{
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);

    // 单元格多选/单选 等逻辑
    useMoveEventListener(({ startPoint, lastPoint, isStart, isEnd }) => {
        const activeTableLayer = activeTableLayerRef.current;
        if (!activeTableLayer) return;

        const activeStageIndex = framework.service.frame.getActiveIndex();
        if (!framework.service.transform.checkPointInFrame(startPoint, activeStageIndex)) return;

        const { cellsSelectedInfo, rowOrColSelectedRectInfo, activeCell } = getState();
        const tableCanSelected = !!activeCell || !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
        if (!tableCanSelected) return;
        if (isStart) {
            moveStartLayerInfo.current = {
                top: activeTableLayer.top,
                left: activeTableLayer.left,
                width: activeTableLayer.width,
                height: activeTableLayer.height,
            };
            return;
        }

        const startCoords = getTableFramePoint(framework.service.transform, startPoint, activeTableLayer);
        const lastCoords = getTableFramePoint(framework.service.transform, lastPoint, activeTableLayer);
        const checkCoordsIsInTableLayer = (coords: IPoint) => {
            const layerInfo = moveStartLayerInfo.current ?? activeTableLayer;
            return (
                coords.x >= layerInfo.left &&
                coords.x <= layerInfo.left + layerInfo.width &&
                coords.y >= layerInfo.top &&
                coords.y <= layerInfo.top + layerInfo.height
            );
        };
        if (!checkCoordsIsInTableLayer(startCoords)) return;

        const cellsIsSelecting = !isStart && !isEnd;

        const newCellsSelectedInfo = getCellsSelectedInfoByMouseRange({
            tableLayer: activeTableLayer,
            range: {
                startCoords: {
                    stageX: startCoords.x,
                    stageY: startCoords.y,
                },
                lastCoords: {
                    stageX: lastCoords.x,
                    stageY: lastCoords.y,
                },
            },
        });
        const newActiveCell = newCellsSelectedInfo && newCellsSelectedInfo.cellInfoList.length > 1 ? null : activeCell;
        setState({
            activeCell: newActiveCell,
            cellsIsSelecting,
            cellsSelectedInfo: newCellsSelectedInfo,
            rowOrColSelectedRectInfo: null,
        });
    });

    // 取消编辑态
    useEffect(() => {
        if (activeCell === null) {
            setState({ hasEditingText: false, baseCellsBeforeEditing: null });
        }
    }, [activeCell]);

    // 菜单
    useEffect(() => {
        if (!hasSelectedInfo) return;
        const layerId = activeTableLayerRef.current?.id;
        if (!layerId) return;
        const unregister = registerTableContextMenu(framework.identity, layerId);
        return unregister;
    }, [hasSelectedInfo]);

    // 如果单元格处于选中态, 则拦截shift多选图层的逻辑, 保证能走到click的shift多选逻辑
    useEffect(() => {
        const mouseDownListenerID = framework.service.editor.onMouseDown(({ clientX, clientY }) => {
            const activeTableLayer = activeTableLayerRef.current;
            if (!activeTableLayer) return;
            const isShowContextMenu = framework.service.editor.contextMenuVisible;
            // 有contextMenu时, 这下点击是关闭contextMenu，所以不做任何处理
            if (isShowContextMenu) return;

            const targetCellInfoList = getTableCellInfoListByPoint({
                point: getTableFramePoint(framework.service.transform, { x: clientX, y: clientY }, activeTableLayer),
                tableLayer: activeTableLayer,
            });

            const targetCellInfo = targetCellInfoList[0];
            if (!targetCellInfo) return;
            const targetCell = activeTableLayer.data.cells[targetCellInfo.vIndex]?.[targetCellInfo.hIndex];
            if (!targetCell) return;

            const { cellsSelectedInfo } = getState();
            const hasSelectedRange = !!cellsSelectedInfo?.cellInfoList.length;
            // shift多选逻辑
            if (isHotKeyPress() && hasSelectedRange) return true;
        }, 9);
        return () => framework.service.events.off(mouseDownListenerID);
    }, []);

    useEffect(() => {
        if (activeTableLayer?.lock) {
            clearTableEditState();
        }
    }, [activeTableLayer?.lock]);

    if (activeTableLayer === null) return null;
    if (activeTableLayer?.lock) return null;

    const handleCellMouseUp = ({ clientX, clientY }: React.MouseEvent<HTMLDivElement>) => {
        const activeTableLayer = activeTableLayerRef.current;
        if (!activeTableLayer) return;
        if (mouseDownActiveTableLayerIdRef.current !== activeTableLayer.id) return;
        const isShowContextMenu = framework.service.editor.contextMenuVisible;
        // 有contextMenu时, 这下点击是关闭contextMenu，所以不做任何处理
        if (isShowContextMenu) return;
        if (!isOnlyClick({ clientX, clientY })) return;

        const targetCellInfoList = getTableCellInfoListByPoint({
            point: getTableFramePoint(framework.service.transform, { x: clientX, y: clientY }, activeTableLayer),
            tableLayer: activeTableLayer,
        });

        const targetCellInfo = targetCellInfoList[0];
        if (!targetCellInfo) return;
        const targetCell = activeTableLayer.data.cells[targetCellInfo.vIndex]?.[targetCellInfo.hIndex];
        if (!targetCell) return;

        // 编辑区 blur 的资源提交是异步的；本次 mouseup 只负责结束编辑，避免新单元格选择抢先卸载编辑器。
        if (getState().hasEditingText && targetCell.__renderId !== getState().activeCell?.id) return;

        const { activeCell, cellsSelectedInfo } = getState();
        const hasSelectedRange = !!cellsSelectedInfo?.cellInfoList.length;
        // shift多选逻辑
        if (isHotKeyPress() && hasSelectedRange) {
            const newCellsSelectedInfo = appendCellsSelectedInfoByCellInfo({
                tableLayer: activeTableLayer,
                originalRect: cellsSelectedInfo!.rect,
                targetCellInfo,
            });
            const newActiveCell =
                newCellsSelectedInfo && newCellsSelectedInfo.cellInfoList.length > 1 ? null : activeCell;
            setState({
                activeCell: newActiveCell,
                cellsSelectedInfo: newCellsSelectedInfo,
                rowOrColSelectedRectInfo: null,
            });
            return;
        }
        if (targetCell.__renderId === activeCell?.id) {
            setState({
                hasEditingText: true,
                baseCellsBeforeEditing: cloneDeep(activeTableLayer.data.baseCells),
            });
            return;
        }

        // 选中其他单元格时，取消当前单元格的编辑态
        if (getState().hasEditingText) {
            setState({
                hasEditingText: false,
                baseCellsBeforeEditing: null,
            });
        }

        const newCellsSelectedInfo: CellsSelectedInfo = {
            rect: {
                startHIndex: targetCell.hIndex,
                endHIndex: targetCell.hIndex + targetCell.colSpan,
                startVIndex: targetCell.vIndex,
                endVIndex: targetCell.vIndex + targetCell.rowSpan,
            },
            cellInfoList: [targetCellInfo],
        };

        setState({
            activeCell: {
                id: targetCell.__renderId,
                hIndex: targetCell.hIndex,
                vIndex: targetCell.vIndex,
            },
            cellsIsSelecting: false,
            cellsSelectedInfo: newCellsSelectedInfo,
            rowOrColSelectedRectInfo: null,
        });
    };

    const getTableController = () => {
        const { baseCells, cells: cellsProp } = activeTableLayer.data;
        const firstCellStyle = getCellStyle(cellsProp[0][0]!, activeTableLayer.data);
        const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
        const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
        const editingWidth =
            hasEditingText && cellEditorWidth?.cellId === activeCellId ? cellEditorWidth?.width : undefined;
        return (
            <>
                {!isBasicOperate && !isMouseWheelSetRowOrColCount && (
                    <>
                        <div
                            className="tableEditCellHitArea"
                            data-layer-id={activeTableLayer.id}
                            data-layer-type={activeTableLayer.layerType}
                            data-operate-type={BaseLayerViewType.MOVE}
                            onMouseUp={handleCellMouseUp}
                        ></div>
                        <TableController
                            tableInfo={pick(activeTableLayer, ['id', 'width', 'height', 'top', 'left', 'rotate'])}
                            tableLayerData={activeTableLayer.data}
                        />
                        <TableCellSelectedRect
                            baseCells={baseCells}
                            style={{
                                ...(editingWidth === undefined ? {} : { width: editingWidth }),
                                zIndex: editingWidth === undefined ? undefined : 4,
                                transform: `translate(${tableFakeBorderLeft}px, ${tableFakeBorderTop}px)`,
                            }}
                        />
                    </>
                )}
                {hasEditingText && activeCell && (
                    <CellEditor
                        layer={activeTableLayer}
                        cellId={activeCell.id}
                        onWidthChange={handleCellEditorWidthChange}
                    />
                )}
                <div
                    className={classNames('tableLayerMoveArea', { 'is-hidden': isBasicOperate })}
                    // 标记为移动操作，避免按下按钮时被识别为空白点击并清空激活态。
                    data-layer-id={activeTableLayer.id}
                    data-operate-type={BaseLayerViewType.MOVE}
                    onMouseDownCapture={() => {
                        setIsMoveHandleDragging(true);
                        framework.service.editor.setDisableMove(false);
                    }}
                >
                    <IconFont name="ArrowFourDirectional" fontSize={18} />
                </div>
            </>
        );
    };

    return (
        <>
            <BaseStagePlugin name="table-layer-edit" zIndex={5}>
                <BaseScaleView layer={framework.service.flatLayer.getLayer(activeTableLayer.id)}>
                    <div
                        ref={setTableEditNode}
                        className="tableEditLayerContent"
                        data-layer-id={activeTableLayer.id}
                        data-layer-type={activeTableLayer.layerType}
                        style={{ '--stage-scale': scale } as React.CSSProperties}
                    >
                        {getTableController()}
                    </div>
                </BaseScaleView>
            </BaseStagePlugin>
        </>
    );
});

const EmitCustomEvents = React.memo(() => {
    const framework = useFramework();

    const hasEditingText = useTableEditState()((s) => s.hasEditingText);
    const selectedInfo = useTableEditState()((s) => ({
        cellsSelectedInfo: s.cellsSelectedInfo,
        rowOrColSelectedRectInfo: s.rowOrColSelectedRectInfo,
    }));
    const oldSelectedInfoRef = useRef(selectedInfo);

    useEffect(() => {
        framework.service.events.emit(TABLE_CONTROLLER_EVENTS.TABLE_EDIT_STATE_CHANGE, hasEditingText);
    }, [hasEditingText]);

    useEffect(() => {
        const isEqual = (a: typeof selectedInfo, b: typeof selectedInfo) => JSON.stringify(a) === JSON.stringify(b);
        if (!isEqual(oldSelectedInfoRef.current, selectedInfo)) {
            framework.service.events.emit(TABLE_CONTROLLER_EVENTS.SELECTED_INFO_UPDATE, selectedInfo);
            oldSelectedInfoRef.current = selectedInfo;
        }
    }, [selectedInfo]);

    const lineIsDraggingState = useTableEditState()((s) => s.lineIsDragging);
    const oldLineIsDraggingState = useRef(lineIsDraggingState);

    useEffect(() => {
        if (oldLineIsDraggingState.current !== lineIsDraggingState) {
            framework.service.events.emit(TABLE_CONTROLLER_EVENTS.LINE_IS_DRAGGING_STATE_UPDATE, lineIsDraggingState);
            oldLineIsDraggingState.current = lineIsDraggingState;
        }
    }, [lineIsDraggingState]);

    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'table-layer-edit',
    render: () => (
        <>
            <TableEditWidget />
            <EmitCustomEvents />
        </>
    ),
    createState() {
        return [tableEditPluginState];
    },
    createInstance({ identity }) {
        return [
            { key: 'TableLayerEdit', value: new TableLayerEdit(identity) },
            { key: 'TableAction', value: new TableAction(identity) },
        ];
    },
};

export default exportPlugin;
